import { VueRouter } from "vue-router/types/router";
import ObjectModelFakeEntry from "~/models/objectModelEntries/objectModelFakeEntry";
import ListView from "~/models/listViews/listView";
import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
import ObjectModelEntriesInfiniteLoader from "~/models/objectModelEntriesInfiniteLoader";
import ListViewProvider from "~/providers/listViewProvider";
import {Logger} from '~/services/logger'
import * as _ from 'lodash'
import InfiniteScrollLoader from '~/infiniteScrollLoader'
import {IModelInfiniteLoader} from '~/interfaces/infiniteScrollInterfaces'
import {Hash} from '~/interfaces/hashInterfaces'

interface IState {
  router: VueRouter,
  handleChangesJobIds: string[],
  ObjectModelEntriesFullyLoaded: boolean,
  objectModelEntriesInfiniteLoader: IModelInfiniteLoader,
  objectModelEntriesTotalCount: number,
  lastRequestParams: object,
  entryRelationsToDestroy: string[],
}

export default function (router?: VueRouter, handleChangesJobIds?: string[]) {
  const listViewProvider = new ListViewProvider(ListView, 'list_view', 'list_views');

  const baseStore: Hash & {state: IState} = {
    namespaced: true,
    state: {
      router: router,
      handleChangesJobIds: handleChangesJobIds,
      ObjectModelEntriesFullyLoaded: false,
      objectModelEntriesInfiniteLoader: new ObjectModelEntriesInfiniteLoader(listViewProvider),
      objectModelEntriesTotalCount: null,
      lastRequestParams: {},
      entryRelationsToDestroy: [], // an array of client ids for the entry relations that should be deleted
    },
    mutations: {
      clearObjectModelEntryRepos(state: IState): void {
        ObjectModelFakeEntry.repo.removeAll();
        ObjectModelEntry.repo.removeAll();
      },
      setObjectModelEntriesFullyLoaded(state: IState, newValue): void {
        state.ObjectModelEntriesFullyLoaded = newValue;
      },
      setObjectModelEntriesTotalCount(state: IState, newValue): void {
        state.objectModelEntriesTotalCount = newValue;
      },
      setLastRequestParams(state: IState, params: object): void {
        state.lastRequestParams = params
      },
      updateHandleChangesJobIds(state: IState, newValue): void {
        state.handleChangesJobIds = newValue;
      },
      addEntryRelationForDeletion(state: IState, entryRelation): void {
        state.entryRelationsToDestroy.push(entryRelation);
      },
    },
    actions: {
      clearObjectModelEntryRepos({ commit, state, getters }): void {
        commit('clearObjectModelEntryRepos');
      },
      async search(
        { commit, state, getters }: { commit: any, state: IState, getters },
        { listView, fake, prefilteredIds, forceNewQuery, ...json }: { listView: ListView, fake: boolean, prefilteredIds: string[], forceNewQuery?: boolean }) {
        const requestParams = { listView, fake, prefilteredIds, forceNewQuery, ...json }

        const normalizedRequestParams = InfiniteScrollLoader.normalizeRequestParameters(requestParams);
        commit('setLastRequestParams', normalizedRequestParams)

        return state.objectModelEntriesInfiniteLoader.fetch(requestParams)
          .then(({ result, objects, isFullyLoaded, isNewQuery }) => {
            /**
             * Solves ListView filter query race condition #178951326
             * Does not take page into account, on purpose, allowing infinite scroll to function properly
             */
            const isCurrentQuery = _.isEqual(normalizedRequestParams, state.lastRequestParams)
            if (isCurrentQuery) {
              Logger.debug('ObjectModelEntriesStore#search', 'response processed', normalizedRequestParams.listView.filterQuery)
            } else {
              Logger.debug('ObjectModelEntriesStore#search', 'response skipped', normalizedRequestParams.listView.filterQuery)
              return
            }
            if (isNewQuery) {
              commit('clearObjectModelEntryRepos');
            }
            objects.forEach(entryJson => {
              const model = fake ? ObjectModelFakeEntry : ObjectModelEntry;
              const entry = model.repo.find(entryJson.id) || new model();
              entry.loadFromJson(entryJson);
            });
            commit('setObjectModelEntriesFullyLoaded', isFullyLoaded);
            commit('setObjectModelEntriesTotalCount', result.total_count);
          });
      },
    },
  };

  return window.AppConfig.mergeJson(
    baseStore,
  );
}